import { assign, Sender } from 'xstate';
import { getDatabase } from 'firebase/database';
import { getStorage } from 'firebase/storage';
import { CreditCardContext, CreditCardEvent } from './creditCard';
import { SubmitCaseEvent, SubmitCaseContext } from './types';
import {
  PaymentType,
  SelectPaymentTypeContext,
  SelectPaymentTypeEvent,
} from './selectPaymentType';
import { CaseTypeContext, CaseTypeEvent } from './caseType';
import {
  CaseDraft,
  saveDraft as saveDraftToFirebase,
  updateDraft,
  deleteDraft,
} from '../../services/firebase/drafts';
import { SUBMIT_CASE_DRAFT_ID } from '../../constants/localStorage';
import { uploadSites } from '../../services/firebase/storage';
import { FormValues } from '../../components/case/CaseForm';
import * as RDBCase from '../../services/buildfire/rdb/cases';

function stripFileObjects(caseInfo: FormValues): FormValues {
  return {
    ...caseInfo,
    sites: caseInfo.sites.map(site => ({
      ...site,
      images: site.images.map(img => {
        if (typeof img === 'object' && 'status' in img && 'originalFile' in img) {
          const { originalFile, ...rest } = img as RDBCase.ImageStatus;
          return rest;
        }
        return img;
      })
    }))
  };
}

export const setCaseInfo = assign<SubmitCaseContext, SubmitCaseEvent>({
  caseInfo: (context, ev: any) => {
    if (ev.type === 'NEXT' || ev.type === 'SET_CASE_INFO') {
      return ev.caseInfo;
    }
    console.warn(
      `WARNING: #setCaseInfo action expected NEXT event but received ${ev.type}`
    );
    return context.caseInfo;
  },
});

export const setUploadedCaseInfo = assign<SubmitCaseContext, SubmitCaseEvent>({
  caseInfo: (context, ev: any) => ev.data,
});

// Credit Card events
export const removePaymentMethod = assign<CreditCardContext, CreditCardEvent>(
  (context, ev) => {
    if (ev.type !== 'REMOVE_CREDIT_CARD') {
      console.warn(
        `WARNING: #setCreditCardInfo action expected NEXT event but received ${ev.type}`
      );
      return context;
    }

    const paymentMethods =
      context.paymentMethods?.filter(({ id }) => id !== ev.paymentMethodId) ||
      [];
    return {
      selectedPaymentMethod:
        context.selectedPaymentMethod === ev.paymentMethodId
          ? paymentMethods[0]?.id
          : context.selectedPaymentMethod,
      paymentMethods,
    };
  }
);

export const selectPaymentMethod = assign<CreditCardContext, CreditCardEvent>({
  selectedPaymentMethod: (context, ev) => {
    if (ev.type !== 'SELECT_CARD') {
      console.warn(
        `WARNING: #setCreditCardInfo action expected NEXT event but received ${ev.type}`
      );
      return context.selectedPaymentMethod;
    }

    return ev.id;
  },
});

export const setInvoiceAccount = assign<CreditCardContext, CreditCardEvent>({
  selectedInvoiceAccountUid: (context, ev) => {
    if (ev.type !== 'SET_INVOICE_ACCOUNT') {
      console.warn(
        `WARNING: #setInvoiceAccount action expected SET_INVOICE_ACCOUNT event but received ${ev.type}`
      );
      return context.selectedInvoiceAccountUid;
    }
    return ev.invoiceAccountUid;
  },
});

export const updateParentAccount = assign<SubmitCaseContext, SubmitCaseEvent>({
  parentAccount: (context, ev) => {
    if (ev.type !== 'PARENT_ACCOUNT_CHANGE') {
      console.warn(
        `WARNING: #updateParentAccount action expected PARENT_ACCOUNT_CHANGE event but received ${ev.type}`
      );
      return context.parentAccount;
    }
    return ev.parentAccount;
  },
});

export const setPaymentType = assign<
  SelectPaymentTypeContext,
  SelectPaymentTypeEvent
>((context, ev) => {
  if (ev.type !== 'SET_PAYMENT_TYPE') {
    console.warn(
      `WARNING: #setPaymentType action expected SET_PAYMENT_TYPE event but received ${ev.type}`
    );
    return context;
  }
  return {
    ...context,
    paymentType: ev.paymentType,
    invoicingEnabled: ev.paymentType === PaymentType.INVOICE,
  };
});

export const setCaseType = assign<CaseTypeContext, CaseTypeEvent>(
  (context, ev) => {
    if (ev.type !== 'SET_CASE_TYPE') {
      console.warn(
        `WARNING: #setCaseType action expected SET_CASE_TYPE event but received ${ev.type}`
      );
      return context;
    }
    return {
      ...context,
      caseType: ev.caseType,
    };
  }
);

export const storeDraft = assign<SubmitCaseContext, any>((context, event) => {
  const draft: CaseDraft | null = event.data;

  return {
    ...context,
    pendingDraft: draft,
  };
});

export const setDraftData = assign<SubmitCaseContext, any>((context) => {
  const draft: CaseDraft | null = context.pendingDraft;

  if (!draft) {
    return {
      ...context,
      isDraftLoaded: true,
      pendingDraft: undefined,
    };
  }

  // Store draft ID in localStorage
  localStorage.setItem(SUBMIT_CASE_DRAFT_ID, draft.id!);

  return {
    ...context,
    draftId: draft.id,
    caseType: draft.caseType,
    caseInfo: draft.caseInfo,
    paymentType: draft.paymentType,
    selectedPaymentMethod: draft.selectedPaymentMethod,
    selectedInvoiceAccountUid:
      draft.selectedInvoiceAccountUid || context.selectedInvoiceAccountUid,
    isDraftLoaded: true,
    pendingDraft: undefined,
  };
});

// Shared helper to delete draft from Firebase and clear localStorage
const deleteDraftData = (context: SubmitCaseContext, draftId: string) => {
  const database = getDatabase(context.firebaseApp);
  return deleteDraft(database, context.userId, draftId)
    .then(() => {
      localStorage.removeItem(SUBMIT_CASE_DRAFT_ID);
    })
    .catch(console.error);
};

// XState action: Clears pendingDraft when user declines to load draft
// Used during initial load when user chooses "Clear and Start Over"
export const clearDraftData = assign<SubmitCaseContext, any>((context) => {
  const draft: CaseDraft | null = context.pendingDraft;

  if (draft?.id) {
    deleteDraftData(context, draft.id);
  }

  return {
    ...context,
    isDraftLoaded: true,
    pendingDraft: undefined,
  };
});

export const handleDraftLoadError = assign<SubmitCaseContext, any>(
  (context, event) => {
    console.warn('Failed to load draft:', event.data);
    return {
      ...context,
      isDraftLoaded: true,
    };
  }
);

// Side effect: Clears saved draftId after successful case submission
// Called when transitioning to 'submitted' state (checkout completion)
export const clearDraft = (context: SubmitCaseContext) => {
  if (!context.draftId || !context.userId) {
    return;
  }

  deleteDraftData(context, context.draftId);
};

export const setUploadProgress = assign<SubmitCaseContext, SubmitCaseEvent>({
  uploadProgress: (context, event) => {
    if (event.type === 'SET_UPLOAD_PROGRESS') {
      return event.data;
    }
    return context.uploadProgress;
  },
});

export const setUploadingDraft = assign<SubmitCaseContext, SubmitCaseEvent>({
  isUploadingDraft: (context, event) => {
    if (event.type === 'SET_UPLOADING_DRAFT') {
      return event.uploading;
    }
    return context.isUploadingDraft;
  },
});

function calculatePercent(num: number, total: number) {
  return Math.min((num + 1) / total, 0.99);
}

export const saveDraftWithProgress = async (
  context: SubmitCaseContext,
  send: Sender<SubmitCaseEvent>
) => {
  if (!context.caseInfo || !context.userId) {
    return;
  }

  try {
    send({ type: 'SET_UPLOADING_DRAFT', uploading: true });

    const database = getDatabase(context.firebaseApp);
    const storage = getStorage(context.firebaseApp);

    // Check if sites need to be uploaded
    const needsUpload = context.caseInfo.sites.some((site) =>
      site.images.some((img) => {
        if (img instanceof File) return true;
        // Also check for failed uploads that need retry
        if (typeof img === 'object' && 'status' in img && img.status === 'failed' && img.originalFile) {
          return true;
        }
        return false;
      })
    );
    let caseInfo: FormValues = context.caseInfo;

    if (needsUpload) {
      send({
        type: 'SET_UPLOAD_PROGRESS',
        data: {
          message: 'Uploading files...',
          percent: 0,
        },
      });

      const numTotalAttachments = context.caseInfo.sites.reduce((acc, site) => {
        const fileCount = site.images.filter((img) => {
          if (img instanceof File) return true;
          // Count failed uploads that need retry
          if (typeof img === 'object' && 'status' in img && img.status === 'failed' && img.originalFile) {
            return true;
          }
          return false;
        }).length;
        return acc + fileCount;
      }, 0);

      // Upload sites with progress tracking
      const uploadResult = await uploadSites(
        storage,
        context.caseInfo.sites,
        context.userId,
        (num, total, name, siteIndex) => {
          const siteNum = siteIndex + 1;
          send({
            type: 'SET_UPLOAD_PROGRESS',
            data: {
              message: `Uploaded ${
                num + 1
              } of ${numTotalAttachments} files (site #${siteNum})`,
              percent: calculatePercent(num, numTotalAttachments),
            },
          });
        }
      );

      // Update case info with uploaded sites (including failures)
      caseInfo = {
        ...context.caseInfo,
        sites: uploadResult.sites,
      };
    }

    send({
      type: 'SET_UPLOAD_PROGRESS',
      data: {
        message: 'Saving draft...',
        percent: 0.99,
      },
    });

    const draft: Omit<CaseDraft, 'id' | 'createdAt' | 'updatedAt'> = {
      caseType: context.caseType,
      caseInfo: stripFileObjects(caseInfo),
      paymentType: context.paymentType,
      selectedPaymentMethod: context.selectedPaymentMethod,
      selectedInvoiceAccountUid: context.selectedInvoiceAccountUid,
    };

    if (context.draftId) {
      // Update existing draft
      await updateDraft(database, context.userId, context.draftId, draft);
    } else {
      // Create new draft
      const draftId = await saveDraftToFirebase(
        database,
        context.userId,
        draft
      );
      localStorage.setItem(SUBMIT_CASE_DRAFT_ID, draftId);
      context.draftId = draftId;
    }

    send({ type: 'SET_UPLOADING_DRAFT', uploading: false });

    return caseInfo;
  } catch (error) {
    console.error('Error saving draft:', error);
    send({ type: 'SET_UPLOADING_DRAFT', uploading: false });
    throw error;
  }
};

export const saveDraftQuick = (context: SubmitCaseContext) => {
  // Only save if we have case info and a draft already exists (payment selections shouldn't create new drafts)
  if (!context.caseInfo || !context.userId || !context.draftId) {
    return;
  }

  const database = getDatabase(context.firebaseApp);
  const draft: Omit<CaseDraft, 'id' | 'createdAt' | 'updatedAt'> = {
    caseType: context.caseType,
    caseInfo: stripFileObjects(context.caseInfo),
    paymentType: context.paymentType,
    selectedPaymentMethod: context.selectedPaymentMethod,
    selectedInvoiceAccountUid: context.selectedInvoiceAccountUid,
  };
  console.log('Saving draft:', draft);

  // Update existing draft silently
  updateDraft(database, context.userId, context.draftId, draft).catch(
    console.error
  );
};

export const updateUrlPage = (pageValue: string) => () => {
  const params = new URLSearchParams(window.location.search);
  params.set('page', pageValue);

  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState(null, '', newUrl);
};
